import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '../core/storage.js';
import { safeLocalStorage } from '../storage/safe.js';

export type ThemeMode = 'light' | 'dark' | 'system';
export type TextScale = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type Density = 'compact' | 'comfortable' | 'spacious';
export type ReducedMotion = 'auto' | 'always' | 'never';

export interface AuthUser {
  id: string;
  email?: string;
  organisationId?: string;
  roles?: string[];
}

export interface AppState {
  // ── auth ───────────────────────────────────────────────────────────
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setTokens: (access: string | null, refresh?: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  clearAuth: () => void;

  // ── i18n ───────────────────────────────────────────────────────────
  locale: string;
  timezone: string;
  resolvedFromJwt: boolean;
  setLocale: (locale: string) => void;
  setTimezone: (tz: string) => void;

  // ── theme ──────────────────────────────────────────────────────────
  themeMode: ThemeMode;
  primary: string;
  secondary: string;
  tertiary: string;
  setThemeMode: (mode: ThemeMode) => void;
  setPrimary: (color: string) => void;
  setSecondary: (color: string) => void;
  setTertiary: (color: string) => void;

  // ── crypto (in-memory only) ────────────────────────────────────────
  encryptionKey: CryptoKey | null;
  setEncryptionKey: (key: CryptoKey | null) => void;
  clearEncryptionKey: () => void;

  // ── ui ─────────────────────────────────────────────────────────────
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  // ── a11y ───────────────────────────────────────────────────────────
  textScale: TextScale;
  density: Density;
  reducedMotion: ReducedMotion;
  highContrast: boolean;
  setTextScale: (scale: TextScale) => void;
  setDensity: (density: Density) => void;
  setReducedMotion: (mode: ReducedMotion) => void;
  setHighContrast: (on: boolean) => void;
}

function detectBrowserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

function detectBrowserLocale(): string {
  try {
    return typeof navigator !== 'undefined' ? navigator.language : 'en';
  } catch {
    return 'en';
  }
}

interface PersistedSlice {
  locale: string;
  timezone: string;
  themeMode: ThemeMode;
  primary: string;
  secondary: string;
  tertiary: string;
  sidebarOpen: boolean;
  textScale: TextScale;
  density: Density;
  reducedMotion: ReducedMotion;
  highContrast: boolean;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setTokens: (access, refresh) =>
        set((s) => ({ accessToken: access, refreshToken: refresh ?? s.refreshToken })),
      setUser: (user) => set({ user }),
      clearAuth: () => set({ accessToken: null, refreshToken: null, user: null }),

      locale: detectBrowserLocale(),
      timezone: detectBrowserTz(),
      resolvedFromJwt: false,
      setLocale: (locale) => set({ locale }),
      setTimezone: (timezone) => set({ timezone }),

      themeMode: 'system',
      primary: '#0d9488',
      secondary: '#6366f1',
      tertiary: '#f59e0b',
      setThemeMode: (themeMode) => set({ themeMode }),
      setPrimary: (primary) => set({ primary }),
      setSecondary: (secondary) => set({ secondary }),
      setTertiary: (tertiary) => set({ tertiary }),

      encryptionKey: null,
      setEncryptionKey: (key) => set({ encryptionKey: key }),
      clearEncryptionKey: () => set({ encryptionKey: null }),

      sidebarOpen: true,
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),

      textScale: 'md',
      density: 'comfortable',
      reducedMotion: 'auto',
      highContrast: false,
      setTextScale: (textScale) => set({ textScale }),
      setDensity: (density) => set({ density }),
      setReducedMotion: (reducedMotion) => set({ reducedMotion }),
      setHighContrast: (highContrast) => set({ highContrast })
    }),
    {
      name: STORAGE_KEYS.theme,
      storage: createJSONStorage(() => ({
        getItem: (key) => {
          const r = safeLocalStorage.getRaw(key);
          return r.kind === 'ok' ? r.value : null;
        },
        setItem: (key, value) => {
          safeLocalStorage.setRaw(key, value);
        },
        removeItem: (key) => {
          safeLocalStorage.remove(key);
        }
      })),
      partialize: (state): PersistedSlice => ({
        locale: state.locale,
        timezone: state.timezone,
        themeMode: state.themeMode,
        primary: state.primary,
        secondary: state.secondary,
        tertiary: state.tertiary,
        sidebarOpen: state.sidebarOpen,
        textScale: state.textScale,
        density: state.density,
        reducedMotion: state.reducedMotion,
        highContrast: state.highContrast
      })
    }
  )
);
